import { useQuery } from '@tanstack/react-query';

import { ApiError, unwrap } from '@/api/envelope';
import { readLinks } from '@/api/generated';
import { useArcOrganizationId } from '@/lib/arcDraft';

import { linksKey } from './keys';
import { toLinkView } from './linkState';

/**
 * Both links, from one read: each page selects its own kind (P5). No range is
 * sent, so the clicks are the backend's default 30 days (P6).
 */
export function useLinks() {
  // The organization staff are viewing, or the signed-in person's own: the ARC's.
  const organizationId = useArcOrganizationId();
  return useQuery({
    queryKey: linksKey(organizationId),
    queryFn: async () => {
      const reply = await readLinks({ throwOnError: true });
      return unwrap(reply.data);
    },
    select: (reply) => ({
      fundraising: toLinkView(reply.fundraising, reply.range),
      volunteer: toLinkView(reply.volunteer, reply.range),
    }),
    enabled: organizationId !== null,
    // The portal turns this off by default. Here a returning admin should see
    // another admin's write, and the form keeps what is being typed (P25).
    refetchOnWindowFocus: true,
  });
}

/**
 * Where a failed read is shown (`docs/error-contract.md`): a 401 belongs to
 * the session handler and is never drawn by the page; a 404 is the page's
 * not-found state; anything else, a server fault, a dropped connection or a
 * 409 no portal handler says, can be retried (P16, NF2, NF3, P40).
 */
export function readFailure(error: unknown): 'elsewhere' | 'not-found' | 'retry' {
  if (error instanceof ApiError) {
    if (error.status === 401) return 'elsewhere';
    if (error.status === 404) return 'not-found';
  }
  return 'retry';
}
